<template>
  <div v-if="renderMain" class="overall-page">

  <div v-if="headsUp" class="headsup-banner" @click="headsUp = false">
    🔔 A new tournament is starting soon — head to chat and <b>!fight</b> to enter! <span class="hu-dismiss">(click to dismiss)</span>
  </div>

  <BotDownBanner :last-mod="isLiveTournament ? tournamentLastMod : null" />

  <SiteNav active="bracket">
    <template #actions>
      <span class="snav-act">
        <button class="settings-gear" @click="showSettings = true" aria-label="Settings">⚙</button>
        <span class="act-tip">Highlight favorite players with colors, notification sounds, display toggles — and replay the intro tour.</span>
      </span>
      <NavLogin :username="prefs.myUsername" />
      <span class="snav-act">
        <a class="help-button" href="?guide" aria-label="Guide">?</a>
        <span class="act-tip">The FFTBG guide — every chat command, how battles work, and the FAQ.</span>
      </span>
      <span class="snav-act">
        <a class="generator-button" href="?build&random" aria-label="Random unit generator">🎲</a>
        <span class="act-tip">Roll a random FFTBG unit in the Builder — force or ban classes and skills, then tweak anything.</span>
      </span>
      <span v-if="previous_tournament_id && !isArchivedTournament" class="snav-act">
        <a class="prev-tourney-button" :href="'/?tournament=' + previous_tournament_id" aria-label="Previous tournament">⏮</a>
        <span class="act-tip">Jump back to the previous tournament's final bracket.</span>
      </span>
    </template>
  </SiteNav>

  <WhatsNewModal v-if="showWhatsNew" @close="closeWhatsNew" @tour="startMainTour" />
  <SpotlightModal v-if="showSpotlight" :updates="spotlightUpdates" @close="closeSpotlight" />
  <OnboardingTour ref="tour" :steps="tourSteps" @done="onTourDone" />



  <SettingsModal v-if="showSettings"
                 :prefs="prefs"
                 @update:prefs="onPrefsUpdate"
                 @close="showSettings = false" />

  <AnnouncementModal v-if="showAnnouncement"
                     :matches="announcementMatches"
                     :skill-drop="skillDropInfo"
                     :settings-hint="announceSettingsHint"
                     :settings-link="true"
                     @dismiss="dismissAnnouncement"
                     @open-settings="openSettingsFromAnnouncement"
                     @scroll-to-player="gotoPlayer" />

  <!-- Floating "my skill modifiers" button + modal — only when the viewer has set
       a handle. Shows the !skills-style breakdown of their owned skills' roll mods. -->
  <button v-if="prefs.myUsername" class="my-mods-fab" @click="showMyMods = true"
          title="Your active skill modifiers — how your owned skills nudge your character roll">⚡ My Mods</button>
  <MyModifiersModal v-if="showMyMods" :handle="prefs.myUsername" :champion="nextChampion" @close="showMyMods = false" />

  <div class="container">
    <div id="app">
      <div v-if="testMode" class="test-mode-banner">TEST MODE — synthetic units. Visit / to return.</div>
      <div v-if="isArchivedTournament" class="archive-strip">
        <span class="archive-label">⏪ Viewing an archived tournament</span>
        <a v-if="previous_tournament_id" class="archive-btn" :href="'/?tournament=' + previous_tournament_id">⏮ Prev</a>
        <a v-if="next_tournament_id" class="archive-btn" :href="'/?tournament=' + next_tournament_id">Next ⏭</a>
        <a class="archive-btn archive-live" href="/">● Back to Live</a>
      </div>
      <RecentTicker v-if="!testMode && prefs.showTicker !== false" />

      <div v-if="goalWidget" class="goal-widget">
        <div class="goal-head">
          <button class="goal-dismiss" @click="dismissGoal" title="Hide this widget — visit your profile to show it again">✕</button>
          <span class="goal-label">🎯 {{ goalWidget.name }}</span>
          <span class="goal-status">{{ goalWidget.status }}</span>
          <a class="goal-profile" :href="'?player=' + encodeURIComponent(prefs.myUsername) + '&tab=achievements'">profile →</a>
        </div>
        <div v-if="goalWidget.rolling" class="goal-rolling">✨ You're rolling a <b>{{ goalWidget.rolling }}</b> right now — that's one you need!</div>
        <div v-if="goalWidget.remaining.length" class="goal-remaining">
          <span class="goal-remain-label">Still need:</span>
          <span v-for="r in goalWidget.remaining.slice(0, 12)" :key="r" class="goal-remain-item" :class="{ hit: r === goalWidget.rolling }">{{ r }}</span>
          <span v-if="goalWidget.remaining.length > 12" class="goal-remain-more">+{{ goalWidget.remaining.length - 12 }} more</span>
        </div>
        <div v-else-if="!goalWidget.rolling" class="goal-hint">Keep playing — {{ goalWidget.status }}</div>
      </div>

<!-- A meme tournament, from the live tournament's meme.txt in FFTBG's file dump (the API can't tell). -->
<div v-if="memeLine && appState === 'ready'" class="lx-meme" v-tip="memeTip">
  <span class="lx-meme-icon" aria-hidden="true">{{ memeLine.icon }}</span>
  <span class="lx-meme-name">{{ memeLine.text }}</span>
  <span class="lx-meme-note">left out of the site's stats</span>
  <a class="lx-meme-more" href="?memes">past memes →</a>
</div>

<!-- The 2026-10 refresh's spotlight: the fight that's on now, big (src/components/spotlight). Sits above the old bracket
     until the new bracket replaces it. -->
<FightSpotlight v-if="appState === 'ready'" :teams="teams" :winners="winners" :maps="maps" :current-match-index="currentMatchIndex"
                :tournament-id="current_tournament_id" :game-data="spotlightData" :featured="spotlightMatch" :champ-streak="champStreak" :lab-request="labRequest" @back-live="spotlightMatch = null" @team="teamCard = $event" />

<!-- Clockwork Coliseum, the crew's other game: a full-width banner between the spotlight and the bracket. -->
<ColiseumPromo v-if="!testMode" wide class="cc-promo" />

<div v-if="!prefs.hideBracket" class="bracket-holder">
  <!-- The 2026-10 refresh's bracket (src/components/bracket/BracketTree.vue). Click a match to put it in the spotlight. -->
  <BracketTree :teams="teams" :winners="winners" :maps="maps" :current-match-index="currentMatchIndex"
               :featured="spotlightMatch" :champ-streak="champStreak"
               :highlight-colors="highlightColors" :game-data="spotlightData"
               @feature="featureMatch" @team="teamCard = $event" @fight="(m) => openLab(m.i)">
    <template #champ-below>
            <div v-if="skillDropInfo" class="skill-drop-chip" :class="'rarity-' + skillDropInfo.rarity"
                 :title="'!buyrandom skill drop for this tournament — ' + skillDropInfo.rarity + (skillDropInfo.category ? ' ' + skillDropInfo.category : '')">
              <span class="sd-label">🎁 Skill Drop</span>
              <span class="sd-name">{{ skillDropInfo.name }}</span>
              <span class="sd-rarity">{{ skillDropInfo.rarity }}</span>
              <span v-if="dropOwned !== null" class="sd-own" :class="dropOwned ? 'owned' : 'missing'">{{ dropOwned ? '✓ you own this' : "you're missing this!" }}</span>
              <span v-if="dropLastText" class="sd-last" :class="{ rare: dropLastText.rare }" :title="dropLastText.title || null">{{ dropLastText.rare && !dropLastText.text.startsWith('not') ? '🫨 ' : '' }}{{ dropLastText.text }}</span>
            </div>
    </template>
  </BracketTree>
</div>





      <!-- 📰 The newest Daily Chocobo, as a front page (src/components/ChocoboFront.vue). -->
      <ChocoboFront />

      <!-- The team section's controls: show/hide each team, and your highlighted players who got in or were snubbed. -->
      <div class="lx-teambar">
        <span class="lx-kicker">Teams</span>
        <div class="tb-chips">
          <button v-for="team in teams" :key="team.name" class="tb-chip" :class="{ hiding: team.hidden }" :style="{ '--tc': teamBrightFor(team.label) }"
                  @click="team.hidden = !team.hidden" :aria-pressed="String(!team.hidden)"
                  :title="(team.hidden ? 'Show ' : 'Hide ') + team.label + ' team'">{{ team.name === 'champion' ? 'Champ' : team.label }}</button>
        </div>
        <!-- the same switch as Settings' "Color skills & gear by win rate", where people can find it -->
        <button class="tb-wr" :class="{ on: prefs.showWinRates }" :aria-pressed="String(!!prefs.showWinRates)" @click="prefs.showWinRates = !prefs.showWinRates"
                v-tip="{ title: 'Win-rate colors', lines: ['Colors each unit’s gear and skills by how often units with it win: green above average, red below, brighter the further off. Skills are compared to the class’s own average.', 'Hover an item for its win rate.'] }">
          <span class="tb-wr-dot"></span>Win-rate colors</button>
        <!-- how much of each unit the team section shows (prefs.teamCards) -->
        <div class="tb-mode" role="group" aria-label="Unit cards">
          <button v-for="[m, label, tip] in TEAM_CARD_MODES" :key="m" :class="{ on: (prefs.teamCards || 'columns') === m }" :aria-pressed="String((prefs.teamCards || 'columns') === m)"
                  @click="prefs.teamCards = m" v-tip="tip">{{ label }}</button>
        </div>
        <div v-if="highlightedEntrants.length || highlightedSnubs.length" class="tb-entries">
          <span v-for="u in highlightedEntrants" :key="'in-'+u.name" class="tb-in">
            <a href="#" class="entrant-link" @click.prevent="gotoPlayer(u.name)" :style="u.color ? { color: u.color } : null">{{ u.name }}</a> is in!
          </span>
          <span v-for="u in highlightedSnubs" :key="'snub-'+u.name" class="tb-snub" :style="u.color ? { color: u.color } : null">{{ u.name }} was snubbed.</span>
        </div>
      </div>

    <!-- The 2026-10 refresh's team section (src/components/bracket/TeamSection.vue); a unit opens the new full card. -->
    <TeamSection :teams="teams" :winners="winners" :game-data="spotlightData" :champ-streak="champStreak" :highlight-colors="highlightColors"
                 :mode="prefs.teamCards || 'columns'" @team="teamCard = $event" />
    <!-- A team's full card: its four units side by side, its opponent and its run (src/components/bracket/TeamCard.vue). -->
    <TeamCard v-if="teamCard && appState === 'ready'" :team-name="teamCard" :teams="teams" :winners="winners" :maps="maps"
              :current-match-index="currentMatchIndex" :game-data="spotlightData" :champ-streak="champStreak" :highlight-colors="highlightColors"
              @close="teamCard = null" />
    



  </div>
  </div>

  </div>
  <div v-else class="loading-screen">
    <h1>Luxor's Wiznaib.us</h1>
    <h2 v-if="appState === 'failed'">Couldn't load required data.</h2>
    <h2 v-else>Currently Awaiting Data…</h2>

    <ul class="source-list">
      <li v-for="src in sourceList" :key="src.key" class="source-row" :class="sources[src.key]">
        <span class="dot"></span>
        <span class="label">{{ src.label }}</span>
        <span class="status">{{ sourceStatus(src.key) }}</span>
      </li>
    </ul>

    <button v-if="appState === 'failed'" @click="reload" class="reload-btn">Reload</button>
  </div>
</template>

<script>


import SettingsModal from './components/SettingsModal.vue';
import AnnouncementModal from './components/AnnouncementModal.vue';
import ChampStreakBadge from './components/ChampStreakBadge.vue';
import ChocoboFront from './components/ChocoboFront.vue';
import MyModifiersModal from './components/MyModifiersModal.vue';

import SiteNav from './components/SiteNav.vue';
import BotDownBanner from './components/BotDownBanner.vue';
import RecentTicker from './components/RecentTicker.vue';
import ColiseumPromo from './components/ColiseumPromo.vue';
import WhatsNewModal from './components/WhatsNewModal.vue';
import NavLogin from './components/NavLogin.vue';
import SpotlightModal from './components/SpotlightModal.vue';
import FightSpotlight from './components/spotlight/FightSpotlight.vue';
import BracketTree from './components/bracket/BracketTree.vue';
import TeamSection from './components/bracket/TeamSection.vue';
import TeamCard from './components/bracket/TeamCard.vue';
import OnboardingTour from './components/OnboardingTour.vue';
import { SITE_UPDATES } from './data/siteUpdates.js';
import { onboardSeen, markOnboardSeen, introForced, resetOnboarding } from './lib/onboarding.js';
import { playEventIfEnabled, teamsWithTrackedUnits, matchEvents, matchTeams } from './lib/sounds.js';
import { buildCharStats, buildItemsChanged, buildAbilitiesInfo, buildNotables } from './lib/apiData.js';

import { loadPrefs, savePrefs, markAnnouncementSeen, resolveHighlightedUsers } from './lib/userPrefs.js';
import { wrPref } from './lib/wrPref.js';
import { freshGoalInfo } from './lib/goalRefresh.js';

// Baked-in highlighted users (Luxor's own crew). Per-user prefs can extend
// or hide this list — see DEFAULT_PREFS / SettingsModal.

import noteables from "./data/notables.json";
import { rosterStats } from "./lib/preload.js";
import { teamBright } from "./lib/teamColors.js";
import genTokens from "./data/genTokens.json";
import { fetchDump, bustDump, fetchChampStreak, parseSkills, ownsToken, fetchTournamentDump, memeLabel, parseNpcs } from "./lib/fftbgLive.js";
import { lastDropAgo } from "./lib/skillDrops.js";
import { unitGrade } from "./lib/unitGrade.js";

// Bundled game-data snapshots — the AUTHORITATIVE source for class stats,
// item stats, and ability info (no live fetch; the old mustad.io API was
// re-publishing FFTBG's own help files with lag). Refresh with
// `node tools/fetchGameData.mjs` (runs automatically in `npm run rebuild`).
import classesBackup from "./data/classes.backup.json";
import itemsBackup from "./data/items.backup.json";
import abilitiesBackup from "./data/abilities.backup.json";
import monsterSkills from "./data/monsterSkills.json";

import { buildTestTeams } from "./testFixtures.js";

import axios from "axios";

// Item/class normalization, safeDict, defaults, and the build helpers live in
// ./lib/apiData.js (imported above) so the offline tools build identical dicts.

const safeNotables = buildNotables(noteables);
// the team bar's unit-card switch: [prefs.teamCards value, label, hover]
const TEAM_CARD_MODES = [
  ['columns', 'Full cards', 'A team per row, its four full cards side by side: gear, and every skill with its damage'],
  ['compact', 'Compact', 'One row per unit; click a unit for its full card'],
];
const bundledCharStats = buildCharStats(classesBackup.classes);
const bundledItemsChanged = buildItemsChanged(itemsBackup.items);
const bundledAbilitiesInfo = buildAbilitiesInfo(abilitiesBackup.abilities);

export default {
  name: "App",
  components: {
    FightSpotlight,
    BracketTree,
    TeamSection,
    TeamCard,
    ChampStreakBadge,
    ChocoboFront,
    MyModifiersModal,
    SettingsModal,
    AnnouncementModal,

    SiteNav,
    BotDownBanner,
    RecentTicker,
    ColiseumPromo,
    WhatsNewModal,
    NavLogin,
    SpotlightModal,
    OnboardingTour,
  },
  data() {
    return {
      TEAM_CARD_MODES,
      // Per-browser user prefs (loaded from localStorage on mount). Shape
      // is defined in src/lib/userPrefs.js — currently:
      //   hideDefaultHighlights, hideBracket, customHighlights[]
      prefs: loadPrefs(),
      showSettings: false,
      // { unit, teamColor } when the full-screen unit card is open, else null.
      spotlightMatch: null, // a match clicked in the bracket, shown in the spotlight (null = the live one)
      showAnnouncement: false,
      // The floating "my skill modifiers" modal (viewer = prefs.myUsername).
      showMyMods: false,
      // The tournament's !buyrandom skill-drop token (e.g. "BraveBoost"), from
      // the API's SkillDrop field. Resolved to name/category/rarity via genTokens.
      skillDrop: null,
      // Whether the viewer (prefs.myUsername) already owns the drop token, from
      // their live userskills dump. null = unknown/not-checked (no handle set, or
      // the fetch is gated/failed — only works on the wiznaib.us origin).
      dropOwned: null,
      // The current champion's title-defense streak (global streak.txt), fetched
      // once per tournament. 0 = unknown/none.
      champStreak: 0,
      // The live tournament's meme type from its folder in FFTBG's file dump ('halloween',
      // 'toc29', …), null for a normal tournament or when the dump can't be read.
      tournamentMeme: null,
      teamCard: null,     // the team whose full team card is open ('red' … 'champion')
      // How long since the current drop token last dropped, from the fftbg.com
      // tournaments list: { ago, lastMod } | { capped, window } | null.
      dropLastSeen: null,
      // Fight view: the two team names to compare, or null when closed.
      labRequest: 0,      // bumped to open the spotlight's Battle lab (the bracket's ⚔, a ?match= link)
      // When the live tournament last changed (the API's LastMod): the bot-down banner
      // uses it to tell whether wiznaibot is hearing the matches being played.
      tournamentLastMod: null,
      // "New tournament starting soon" heads-up banner + its poll timers.
      headsUp: false,
      pollTimer: null,
      goalTimer: null,
      headsUpTimer: null,
      // Last poll snapshot { matchIndex, winners } for match-sound detection.
      soundSnapshot: null,
      // Whether the URL is the live tournament (no ?tournament= override).
      // The start-of-tournament announcement only fires for live loads —
      // re-opening an archived tournament link shouldn't surprise the user
      // with a "X is in!" popup for events that already happened.
      isLiveTournament: !new URLSearchParams(window.location.search).get('tournament'),
      items: [],
      // The nine fixed FFTBG teams. Each entry holds its roster (the API's
      // Teams[name].Units), a `hidden` flag (toggled by the team bar and the
      // auto-hide-on-loss watcher), a capitalized `label` ("Red"/"Blue"/…/
      // "Champion"). Order is the
      // bracket order (QF1 left/right, QF2 left/right, etc.).
      teams: [
        { name: 'red',      label: 'Red',      hidden: false, roster: [], palettes: '' },
        { name: 'blue',     label: 'Blue',     hidden: false, roster: [], palettes: '' },
        { name: 'green',    label: 'Green',    hidden: false, roster: [], palettes: '' },
        { name: 'yellow',   label: 'Yellow',   hidden: false, roster: [], palettes: '' },
        { name: 'white',    label: 'White',    hidden: false, roster: [], palettes: '' },
        { name: 'black',    label: 'Black',    hidden: false, roster: [], palettes: '' },
        { name: 'purple',   label: 'Purple',   hidden: false, roster: [], palettes: '' },
        { name: 'brown',    label: 'Brown',    hidden: false, roster: [], palettes: '' },
        { name: 'champion', label: 'Champion', hidden: false, roster: [], palettes: '' },
      ],
      entrants: [],
      // Game metadata, built from the bundled snapshots at module load (see
      // the imports above) — available synchronously, no network involved.
      charStats: bundledCharStats,
      itemschanged: bundledItemsChanged,
      maps: [],
      current_tournament_id : "",
      previous_tournament_id : "",
      next_tournament_id : "",
      winners: [],
      // Per-API load state: 'loading' | 'loaded' | 'failed'. The tournament
      // (the only required source) gates the main render via appState; the
      // optional recent-list loads in the background and degrades gracefully.
      // (Class/item/ability data is all bundled — see charStats above — so
      // none of it appears here.)
      sources: {
        tournament: 'loading',
        recent: 'loading',
      },
      sourceErrors: {
        tournament: '', recent: '',
      },
      // Display order + labels for the loading screen.
      sourceList: [
        { key: 'tournament', label: 'Tournament Data (fftbg)' },
        { key: 'recent', label: 'Previous Tournaments (fftbg)' },
      ],
      snubs: [],
      // Map of ability name -> `info` string, used by InlineUnit to
      // derive ability damage formulas (lib/abilityFormula.js).
      abilitiesInfo: bundledAbilitiesInfo,
      notAbles : safeNotables,
      // True when the page is loaded with ?test=1. Skips the tournament API
      // and synthesizes a fixture-tournament via testFixtures.js.
      testMode: false,
      // The actual gate for showing the main page. Set 150ms after appState
      // becomes 'ready' so the user sees the last source's dot flip green
      // before the v-if/v-else swap removes the loading screen.
      renderMain: false,
      showWhatsNew: false,
      showSpotlight: false,
      forceSpotlight: false,
      tourSteps: [
        { target: '.sp', title: 'The fight on now', body: 'The current match, big, with both teams’ grades and the odds in words. Click a unit to see what it does to the other team; ‹ › browse the other matches.' },
        { target: '.sp-labbar', title: '⚔ Battle lab', body: 'Simulate the match on its map, watch one battle play out turn by turn, and see the map in 3D.' },
        { target: '.bt', title: 'The bracket', body: 'Every match on its map. Click one to put it in the spotlight above, ⚔ for its Battle lab, or a team’s name for its full team card.' },
        { target: '.ts', title: 'Teams', body: 'Every team with its grade and how its tournament is going. Click a unit for its full card — stats, gear, abilities against the team it faces, and grades.' },
        { target: '.snav-players', title: '👥 Players', body: '17k-player directory: career stats, records, betting, and full profiles with championship histories and every unit fielded.' },
        { target: '.snav-quests', title: '🎖️ Quests', body: 'Every quest in one place — Monster, Everything, Miku, VIP, Gift and more — with where you stand on each and this week’s finishers.' },
        { target: '.snav-champions', title: '📜 History', body: 'The championship record: the Tournament of Champions leaderboard, every reign in order, season by season, the best teams ever, and the meme tournaments.' },
        { target: '.snav-game', title: '🎮 Game', body: 'The reference pages: classes, abilities, the armory, all 138 maps in 3D, Stats (win rates by class, skill and gear), and 📚 Learn: the guide, battle mechanics and how the AI thinks.' },
        { target: '.settings-gear', title: '⚙ Settings', body: 'Set your username for a 👤 profile button + goal tracker, highlight favorite players, toggle notification sounds, and replay this tour.' },
        { target: '.generator-button', title: '🎲 Random generator', body: 'Jump to the Builder with a random FFTBG unit — force or ban classes, then edit anything.' },
        { target: '.help-button', title: '❔ Guide', body: 'A searchable FFTBG reference — every chat command and the game mechanics behind the numbers.' },
      ],
      TOURNAMENT_API_URL : "https://fftbg.com/api/tournament/latest",
      TOURNAMENT_API_PREFIX : "https://fftbg.com/api/tournament/",
    };
  },

  computed: {
    // Feature spotlight: updates the user hasn't seen yet.
    unseenUpdates() {
      const seen = new Set(this.prefs.seenUpdates || []);
      return SITE_UPDATES.filter((u) => u.spotlight !== false && !seen.has(u.id));
    },
    // What to show in the spotlight modal: unseen updates, or all spotlightable if ?spotlight forced.
    spotlightUpdates() {
      const spotlightable = SITE_UPDATES.filter((u) => u.spotlight !== false);
      return this.forceSpotlight && !this.unseenUpdates.length ? spotlightable : this.unseenUpdates;
    },
    // Goal tracker widget — reads from prefs (set on the player profile page).
    goalWidget() {
      const g = this.prefs.activeGoal, info = this.prefs.goalInfo;
      if (!g || !info || !this.prefs.myUsername || this.prefs.goalDismissed) return null;
      // Is the user rolling a remaining target right now? Gendered quests list "Lancer ♂",
      // the others the bare class, and the zodiac quest signs. Carried by Monsters counts
      // teammates' monsters, never your own, so your own unit can't be one it needs.
      let rolling = null;
      if (info.remaining && info.remaining.length && !(g.type === 'quest' && g.id === 'carried')) {
        const byLower = new Map(info.remaining.map((r) => [r.toLowerCase(), r]));
        for (const { unit } of this.findUnits(this.prefs.myUsername)) {
          const labels = g.type === 'quest' && g.id === 'zodiac' ? [unit.Sign || '']
            : [unit.Class, unit.Class + (unit.Gender === 'Male' ? ' ♂' : unit.Gender === 'Female' ? ' ♀' : '')];
          rolling = labels.map((l) => byLower.get(l.toLowerCase())).find(Boolean) || null;
          if (rolling) break;
        }
      }
      return { name: info.name, status: info.status, remaining: info.remaining || [], rolling };
    },
    // 'loading' until the tournament (the only required source) resolves;
    // 'failed' if it errors. Optional sources (tips, recent) don't block.
    // Class/item/ability data is bundled, so it can't fail or delay.
    appState() {
      if (this.sources.tournament === 'failed') return 'failed';
      if (this.sources.tournament === 'loaded') return 'ready';
      return 'loading';
    },
    // True when the URL pins a specific (?tournament=) archived tournament
    // rather than the live one — drives the archive-nav strip (Prev/Next/Live).
    isArchivedTournament() { return !this.isLiveTournament && !!this.current_tournament_id; },
    // The meme strip's words: "🎃 Halloween meme tournament", "🃏 Clone Wars meme tournament",
    // "🏆 Tournament of Champions 29" (a ToC or its preshow is already called a tournament).
    memeLine() {
      const label = memeLabel(this.tournamentMeme);
      if (!label) return null;
      const lead = label.match(/^(\p{Extended_Pictographic}\S*)\s+(.*)$/u);
      const name = lead ? lead[2] : label;
      const toc = /^(tournament of champions|toc \d+ preshow)/i.test(name);
      return { icon: lead ? lead[1] : toc ? '🏆' : '🃏', text: toc ? name : name + ' meme tournament' };
    },
    memeTip() {
      return {
        kicker: 'Meme tournament', title: this.memeLine ? this.memeLine.text : '',
        lines: [
          'FFTBG runs special rulesets now and then. This one is marked “' + this.tournamentMeme + '” in FFTBG’s own tournament files.',
          'Meme tournaments are left out of everything this site counts: win rates, grades, player records and quests.',
          'The grades and odds on this page come from normal tournaments, so take them with a grain of salt here.',
        ],
        foot: 'Past meme tournaments: the 🃏 Meme Tournaments page',
      };
    },
    // the game data the spotlight's sim, grades and unit panels read (the same dicts the cards get)
    spotlightData() { return { charStats: this.charStats, itemschanged: this.itemschanged, notAbles: this.notAbles, abilitiesInfo: this.abilitiesInfo, monsterSkills }; },
    // a team's `name` → its team object (roster / hidden / label)
    teamByName() {
      const map = {};
      for (const t of this.teams) map[t.name] = t;
      return map;
    },
    // The champion an entry made now would face, for My Mods' VIP helper: whoever wins this
    // tournament's champion match. Until that's played it's the champion, if they hold the belt.
    nextChampion() {
      const w = this.winners[7], won = w && w !== 'champion' && this.teamByName[w];
      if (won) return { units: won.roster, label: won.label, decided: true, isNew: true };
      const champ = this.teamByName.champion;
      return { units: champ ? champ.roster : [], label: 'Champion', decided: !!w, isNew: false };
    },
    // The tournament's skill drop resolved to { token, name, category, rarity }.
    // Falls back to a spaced token name + common rarity for any unmapped token.
    skillDropInfo() {
      if (!this.skillDrop) return null;
      const meta = genTokens.byToken[this.skillDrop];
      const name = meta?.name || this.skillDrop.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
      return { token: this.skillDrop, name, category: meta?.category || '', rarity: meta?.rarity || 'common' };
    },
    // "last dropped N tournaments ago" line for the chip. A long gap (or no prior
    // drop in the window) reads as a rare event — flagged via `.rare` styling.
    dropLastText() {
      const d = this.dropLastSeen;
      if (!d) return null;
      if (d.capped) return { text: `not dropped in ${d.window}+ tournaments`, rare: true, title: '' };
      return {
        text: `last dropped ${d.ago} tournament${d.ago === 1 ? '' : 's'} ago`,
        rare: d.ago >= 200,
        title: d.lastMod ? 'Previously dropped ' + new Date(d.lastMod).toLocaleDateString() : '',
      };
    },
    // The combined set of highlighted users — defaults (Luxor + friends, unless
    // toggled off) plus the user's customHighlights, deduped by lowercased name
    // (custom overrides default). Each is { name, color }; defaults carry the
    // default highlight color so they're visible out of the box.
    highlightedUsers() {
      return resolveHighlightedUsers(this.prefs);
    },
    // Lowercase the entrants/snubs once so each highlight match is a cheap
    // Set lookup instead of repeatedly toLowercase'ing on every comparison.
    // The fftbg API returns usernames with the joining user's display-name
    // casing, which may not match what the modal-user typed — so we
    // compare in lowercase on both sides.
    entrantsLower() { return new Set(this.entrants.map(e => (e || '').toLowerCase().replace(/_/g, ' '))); },
    snubsLower()    { return new Set(this.snubs.map(s    => (s || '').toLowerCase().replace(/_/g, ' '))); },
    // Highlighted users who are entered in the current tournament, in the
    // same order as `highlightedUsers`. Drives the "X is in!" callouts.
    highlightedEntrants() {
      return this.highlightedUsers.filter(u => this.entrantsLower.has(u.name));
    },
    // Same idea for snubs.
    highlightedSnubs() {
      return this.highlightedUsers.filter(u => this.snubsLower.has(u.name));
    },
    // Combined entrant + snub matches for the start-of-tournament
    // announcement modal. Each entry carries a `status` field so the
    // modal can pick "is in!" vs "was Snubbed.".
    announcementMatches() {
      return [
        ...this.highlightedEntrants.map(u => ({ ...u, status: 'in', units: this.findUnits(u.name) })),
        ...this.highlightedSnubs.map(u    => ({ ...u, status: 'snubbed' })),
      ];
    },
    // Footer hint on the announcement modal pointing fresh visitors at the
    // ⚙ Settings — only while their highlight prefs are untouched (anyone
    // who's added a highlight or set their username has already found it).
    announceSettingsHint() {
      return !this.prefs.customHighlights.length && !this.prefs.myUsername;
    },
    // Map of lowercase-username → color for every highlighted user (defaults
    // included — they now carry the default highlight color), passed as a prop
    // to the bracket, spotlight, team section and cards so they color the unit-name text
    // wherever it renders. Deduped via highlightedUsers.
    highlightColors() {
      const m = new Map();
      for (const u of this.highlightedUsers) if (u.color) m.set(u.name, u.color);
      return m;
    },
    // Index (0-7) of the next undecided match — the one currently being
    // played — used to glow that bracket cell. -1 once every match is
    // decided (so completed/archived tournaments don't glow). Match order:
    // QF 0-3, SF 4-5, Final 6, Champ 7 (matches the maps/winners indices).
    currentMatchIndex() {
      for (let i = 0; i < 8; i++) {
        if (!this.winners[i]) return i;
      }
      return -1;
    },
  },

  watch: {
        // A new tournament: the spotlight follows its live match again.
        current_tournament_id: function() {
          this.spotlightMatch = null;
        },
        // Slight pause between "everything required is loaded" and the
        // loading-screen → main-page swap so the user actually sees the
        // last source's dot flip green before the loading screen unmounts.
        appState: function(newVal) {
          if (newVal === 'ready') {
            setTimeout(() => {
              this.renderMain = true;
              const forced = introForced();
              if (forced) resetOnboarding();  // ?intro replays everything, incl. the sub-page tours next visit
              this.forceSpotlight = new URLSearchParams(window.location.search).has('spotlight');
              if (forced || !onboardSeen('intro')) {
                this.showWhatsNew = true;  // first visit (or ?intro) → welcome; mark all current updates as seen
              } else if (this.forceSpotlight || this.unseenUpdates.length) {
                this.showSpotlight = true;  // returning user with new features → spotlight
              } else {
                this.maybeShowAnnouncement();
              }
            }, 150);
          }
        },

        // Persist prefs to localStorage on any change — the settings modal
        // mutates a deep clone and emits whole-object updates, but the
        // announcement-dismiss flow mutates seenAnnouncements directly.
        // Watching deep covers both paths.
        prefs: {
          deep: true,
          handler(newVal) { savePrefs(newVal); },
        },
        // the win-rate colors' switch, shared with everything on the page that colors by it (lib/wrPref.js)
        'prefs.showWinRates': { immediate: true, handler(v) { wrPref.on = !!v; } },

        // Re-check drop ownership + how-long-since-last-dropped when the drop
        // changes (new tournament) or the viewer sets/changes their handle.
        // immediate so it fires on first load.
        skillDrop: { immediate: true, handler() { this.checkDropOwnership(); this.loadDropHistory(); } },
        'prefs.myUsername': function() { this.checkDropOwnership(); },

        // a new live match takes the spotlight back from a match clicked in the bracket
        currentMatchIndex() { this.spotlightMatch = null; },
        winners: function(newVal, oldVal) {
          // Auto-hide losing teams as winners come in.
          // Live polling reassigns `winners` every 60s; without this guard a
          // team the user manually un-hid would be silently re-hidden each tick.
          if (oldVal && JSON.stringify(newVal) === JSON.stringify(oldVal)) return;
          const w = this.winners;
          const hide = (name) => {
            const t = this.teamByName[name];
            if (t) t.hidden = true;
          };
          const qfPairs = [['red','blue'], ['green','yellow'], ['white','black'], ['purple','brown']];
          qfPairs.forEach((pair, i) => {
            if (w[i] === pair[0]) hide(pair[1]);
            if (w[i] === pair[1]) hide(pair[0]);
          });
          if (w[4] === 'red'    || w[4] === 'blue')   { hide('green');  hide('yellow'); }
          if (w[4] === 'green'  || w[4] === 'yellow') { hide('red');    hide('blue');   }
          if (w[5] === 'white'  || w[5] === 'black')  { hide('purple'); hide('brown');  }
          if (w[5] === 'purple' || w[5] === 'brown')  { hide('white');  hide('black');  }
          if (['red','blue','green','yellow'].includes(w[6])) {
            ['white','black','purple','brown'].forEach(hide);
          }
          if (['white','black','purple','brown'].includes(w[6])) {
            ['red','blue','green','yellow'].forEach(hide);
          }
        },
    },

  async created() {
    // Quietly prefetch the top-50 champions (small, ~170KB) a few seconds after
    // load, so the 🏆 Champions page opens instantly from sessionStorage.
    setTimeout(() => {
      try {
        if (sessionStorage.getItem('fftbgChampsTop50')) return;
        axios.get('https://fftbg.com/api/champions/?limit=50', { timeout: 12000 })
          .then((r) => { if (Array.isArray(r.data) && r.data.length) sessionStorage.setItem('fftbgChampsTop50', JSON.stringify(r.data)); })
          .catch(() => {});
      } catch (e) { /* sessionStorage unavailable */ }
    }, 2500);
    const urlParams = new URLSearchParams(window.location.search);
    // ?test=1 activates the synthetic-tournament fixture (testFixtures.js).
    // We still hit the classes + items APIs so the test units render with
    // real stats; we just skip tournament/tips/recent and synthesize the
    // tournament shape after items load.
    this.testMode = urlParams.get('test') === '1';

    if (this.testMode) {
      this.sources.tournament = 'loaded';
      this.sources.recent = 'loaded';
    } else {
      this.OLD_TOURNAMENT_ID = urlParams.get("tournament");
      if (this.OLD_TOURNAMENT_ID) {
        this.TOURNAMENT_API_URL = this.TOURNAMENT_API_PREFIX + this.OLD_TOURNAMENT_ID;
      }
      await this.loadTournament();
      // ?tournament=<id>&match=<0–7> (a bet's link on the profile's Gambling tab) puts that match in the
      // spotlight with its Battle lab open.
      const linked = urlParams.has('match') ? Number(urlParams.get('match')) : -1;
      if (this.OLD_TOURNAMENT_ID && linked >= 0 && linked <= 7 && matchTeams(linked, this.winners)) this.openLab(linked);

    try {
      // Derive the prev/next tournament ids from the recent-25 list (newest
      // first). findIndex returns -1 when the current tournament isn't in the
      // list, in which case there's no usable neighbor — leave the fields empty
      // so the prev button + archive strip controls hide. idx+1 is older
      // (previous), idx-1 is newer (next).
      const res = await axios.get(`https://fftbg.com/api/tournaments?limit=25`, { timeout: 10000 });
      const idList = res.data.map(t => t.ID);
      const idx = idList.findIndex(id => id == this.current_tournament_id);
      this.previous_tournament_id = (idx >= 0 && idx + 1 < idList.length) ? idList[idx + 1] : '';
      this.next_tournament_id = (idx > 0) ? idList[idx - 1] : '';
      this.sources.recent = 'loaded';
    } catch (error) {
      this.sources.recent = 'failed';
      this.sourceErrors.recent = error.message || String(error);
    }
    } // end of: if (!this.testMode)



    // Test fixture: itemschanged is bundled, so it's always populated.
    if (this.testMode) {
      const fake = buildTestTeams(this.itemschanged);
      this.current_tournament_id = fake.ID;
      this.items = fake.Teams;
      this.maps = fake.Maps;
      for (const team of this.teams) {
        team.roster = fake.Teams[team.name]?.Units || [];
        team.palettes = fake.Teams[team.name]?.Palettes || '';
      }
      this.winners = fake.Winners;
      this.entrants = fake.Entrants;
      this.snubs = fake.Snubs;
    }

    // Keep the goal widget current: rebuild the saved goal whenever a newer data
    // build is out, and re-check every 10 minutes for a tab left open across refreshes.
    if (!this.testMode) {
      this.refreshGoal();
      this.goalTimer = setInterval(() => this.refreshGoal(), 10 * 60 * 1000);
    }

    // Live tournaments only: poll the bracket every minute so winners,
    // the current-match glow, and the team grid update without a refresh.
    if (!this.testMode && !this.OLD_TOURNAMENT_ID) {
      // Seed the match-sound snapshot from the initial load so the first poll
      // only reacts to changes since now (no sounds for already-decided matches).
      this.soundSnapshot = { matchIndex: this.currentMatchIndex, winners: [...this.winners] };
      this.pollTimer = setInterval(() => this.pollTournament(), 60000);
    }
  },

  beforeUnmount() {
    clearInterval(this.pollTimer);
    clearInterval(this.goalTimer);
    clearTimeout(this.headsUpTimer);
  },

  methods: {

    // Does the viewer already own the tournament's skill drop? Reads their live
    // userskills dump (prefs.myUsername) and checks for the drop token. Sets
    // dropOwned true/false, or leaves it null when there's no handle, no drop, or
    // the fetch is unavailable (the dump's CORS only allows the wiznaib.us origin,
    // so this is a no-op on staging/local). Guards against the drop changing
    // mid-fetch.
    async checkDropOwnership() {
      const me = (this.prefs.myUsername || '').trim();
      const drop = this.skillDrop;
      this.dropOwned = null;
      if (!me || !drop) return;
      const text = await fetchDump('userskills', me);
      if (text == null || this.skillDrop !== drop) return;
      this.dropOwned = ownsToken(parseSkills(text), drop);
    },

    // The current champion's title-defense streak (global, prod-only). Left at 0
    // when the fetch is unavailable so the badge just doesn't show. Archived
    // (?tournament=) views skip it entirely — streak.txt is the LIVE champion's
    // count, which has nothing to do with whoever was champion back then.
    async loadChampStreak() {
      const preview = +new URLSearchParams(window.location.search).get('streak');   // ?streak=12 shows the badge for a look
      if (preview > 0) { this.champStreak = preview; return; }
      if (!this.isLiveTournament) return;
      const n = await fetchChampStreak(this.current_tournament_id);
      if (n != null) this.champStreak = n;
    },

    // The live tournament's meme type and NPC filler units, from its folder in FFTBG's
    // file dump (the API says Type "main" for everything and doesn't mark filler). One
    // read per tournament; the folder moves to archive/ after, so archived (?tournament=)
    // views skip it, and ?test=1 never gets here. Prod-only, like the dumps.
    // ?meme=halloween&npcs=Red 1,Blue 3 shows them for a look.
    async loadTournamentDump() {
      this.tournamentMeme = null;
      const q = new URLSearchParams(window.location.search);
      let dump;
      if (q.has('meme') || q.has('npcs')) {
        dump = { meme: q.get('meme') || null, npcs: parseNpcs((q.get('npcs') || '').replace(/,/g, '\n')) };
      } else {
        if (!this.isLiveTournament) return;
        const id = this.current_tournament_id;
        dump = await fetchTournamentDump(id);
        if (!dump || this.current_tournament_id !== id) return;   // a new tournament landed mid-fetch
      }
      this.tournamentMeme = dump.meme;
      // Filler units get an `Npc` flag beside the API's own RaidBoss, for the spotlight and team section.
      for (const { team, index } of dump.npcs) {
        const u = this.teamByName[team]?.roster?.[index];
        if (u) u.Npc = true;
      }
    },

    // How long since the current drop token last dropped. One call to the
    // fftbg.com tournaments list (our normal CORS-friendly API — works on every
    // origin), once per drop change. No prior occurrence in the window → "capped".
    async loadDropHistory() {
      const token = this.skillDrop;
      this.dropLastSeen = null;
      if (!token) return;
      const WINDOW = 300;  // ~12 days; the avg drop gap is ~165, so most are still exact (~164KB vs 273KB at 500)
      try {
        const res = await axios.get(`https://fftbg.com/api/tournaments?limit=${WINDOW}`, { timeout: 10000 });
        if (this.skillDrop !== token) return;  // a new tournament landed mid-fetch
        this.dropLastSeen = lastDropAgo(res.data, token) || { capped: true, window: WINDOW };
      } catch { /* leave null — the line just doesn't show */ }
    },

    // The roster a given team is matched against RIGHT NOW, for the per-spell
    // damage hover on its unit cards. Follows the live bracket rather than the
    // fixed QF pairing: a team's current opponent is the other competitor in the
    // first undecided match it's in. Deeper-round slots resolve from `winners`
    // (SF = QF winners, Final = SF winners, Champ = Final winner vs Champion).
    // Returns [] when the opponent isn't decided yet (or the team is out), so the
    // preview never shows a stale round-1 enemy after the team has advanced.
    opponentRoster(teamName) {
      const w = this.winners || [];
      const matches = [
        ['red', 'blue'], ['green', 'yellow'], ['white', 'black'], ['purple', 'brown'],
        [w[0], w[1]], [w[2], w[3]], [w[4], w[5]], [w[6], 'champion'],
      ];
      const roster = (name) => (name && this.teamByName[name] && this.teamByName[name].roster) || [];
      for (let i = 0; i < matches.length; i++) {
        if (w[i]) continue;                                 // match already decided
        const [a, b] = matches[i];
        if (a === teamName) return roster(b);
        if (b === teamName) return roster(a);
      }
      return [];
    },

    // Fetch the tournament and (re)populate teams/maps/rosters/winners/etc.
    // Used on mount and by the live poll. Returns whether the tournament ID
    // changed (a brand-new tournament appeared).
    async loadTournament() {
      try {
        const res = await axios.get(this.TOURNAMENT_API_URL, { timeout: 10000 });
        const prevId = this.current_tournament_id;
        const changed = !!prevId && res.data.ID !== prevId;
        this.current_tournament_id = res.data.ID;
        if (changed) {
          for (const t of this.teams) t.hidden = false;
        }
        // Rosters/maps/entrants are immutable once the API exposes a tournament
        // (entries lock first), so only swap them on first load or a new ID.
        // Re-assigning fresh identities every poll wiped per-card tuning state
        // (InlineUnit's unit watcher resets on identity change) and re-rendered
        // the whole grid each minute on identical data.
        if (!prevId || changed) {
          this.items = res.data.Teams;
          this.maps = res.data.Maps;
          for (const team of this.teams) {
            team.roster = this.items[team.name]?.Units || [];
            team.palettes = this.items[team.name]?.Palettes || '';   // "Primary/Alt" team colors
          }
          this.entrants = res.data.Entrants;
          this.snubs = res.data.Snubs;
          this.loadChampStreak();  // global champ streak — once per tournament
          this.loadTournamentDump();  // meme type + NPC filler, from FFTBG's file dump — once per tournament
        }
        // Winners + skill drop are the live parts — but keep the array identity
        // stable too, swapping only when a result actually landed.
        if (JSON.stringify(res.data.Winners) !== JSON.stringify(this.winners)) {
          this.winners = res.data.Winners;
        }
        this.skillDrop = res.data.SkillDrop || null;
        this.tournamentLastMod = res.data.LastMod || null;
        if (changed) {
          // New tournament — the viewer's owned skills may have changed (skills
          // turn over each tournament), so drop the cached read and re-grade the
          // drop against a fresh fetch. (The skillDrop watcher covers the case
          // where the drop token itself also changed; this covers a same-token
          // drop across tournaments.)
          bustDump('userskills', this.prefs.myUsername);
          this.checkDropOwnership();
        }
        this.sources.tournament = 'loaded';
        return { ok: true, changed };
      } catch (error) {
        this.sources.tournament = 'failed';
        this.sourceErrors.tournament = error.message || String(error);
        return { ok: false };
      }
    },

    // One poll tick: refresh the bracket. On a brand-new tournament, re-fire
    // the "X is in!" announcement. When the current tournament just finished,
    // pop the "next one starting soon" heads-up.
    async pollTournament() {
      const wasComplete = this.currentMatchIndex === -1;
      const result = await this.loadTournament();
      if (!result.ok) return;
      this.checkMatchSounds();  // tracked-unit match-up / win / loss / champ
      if (result.changed) {
        this.maybeShowAnnouncement();
        return;
      }
      if (!wasComplete && this.currentMatchIndex === -1) {
        this.announceNewTournamentSoon();
      }
    },

    // Heads-up that the current tournament finished — the next join window is
    // opening. Banner always; sound only if the user enabled that event.
    announceNewTournamentSoon() {
      this.headsUp = true;
      clearTimeout(this.headsUpTimer);
      this.headsUpTimer = setTimeout(() => { this.headsUp = false; }, 45000);
      playEventIfEnabled(this.prefs, 'newTournament');
    },

    // Diff this poll's bracket state against the last one and play a sound for
    // each tracked-unit event (match becoming current, a result, a champ win).
    // Seeds the snapshot on the first call (no sound) so we only react to live
    // changes, not the initial load.
    checkMatchSounds() {
      const snap = { matchIndex: this.currentMatchIndex, winners: [...this.winners] };
      const tracked = new Set(this.highlightedUsers.map((u) => u.name));
      const myTeams = teamsWithTrackedUnits(this.items, tracked);
      if (this.soundSnapshot && myTeams.size) {
        for (const ev of matchEvents(this.soundSnapshot, snap, myTeams)) {
          playEventIfEnabled(this.prefs, ev);
        }
      }
      this.soundSnapshot = snap;
    },


    sourceStatus(key) {
      const s = this.sources[key];
      if (s === 'loaded') return '✓ loaded';
      if (s === 'failed') return `FAILED — ${this.sourceErrors[key] || 'unknown error'}`;
      return 'loading…';
    },

    reload() {
      window.location.reload();
    },

    // Receives the modal's edited prefs and replaces ours. The deep
    // watcher on `prefs` persists the change to localStorage.
    onPrefsUpdate(newPrefs) {
      this.prefs = newPrefs;
    },
    dismissGoal() {
      this.prefs.goalDismissed = true;
      savePrefs(this.prefs);
    },
    // Rebuild the goal widget's saved copy if newer data is out (see lib/goalRefresh.js).
    // The deep prefs watcher saves it.
    async refreshGoal() {
      if (this.prefs.goalDismissed) return;
      const info = await freshGoalInfo(this.prefs).catch(() => undefined);
      if (info !== undefined) this.prefs.goalInfo = info;
    },

    // Called after renderMain flips true. Shows the big "X is in!" /
    // "X was Snubbed!" modal iff: live tournament (not an archived URL,
    // not ?test=1), announcements aren't disabled, we have a current
    // tournament ID we haven't shown the modal for, and at least one
    // highlighted user is in entrants or snubs.
    maybeShowAnnouncement() {
      if (this.testMode) return;
      if (!this.isLiveTournament) return;
      if (this.prefs.disableAnnouncements) return;
      if (!this.current_tournament_id) return;
      if (this.prefs.seenAnnouncements.includes(this.current_tournament_id)) return;
      if (!this.announcementMatches.length) return;
      this.showAnnouncement = true;
    },
    // Find an entrant's unit (+ its team label) across the loaded teams, for the
    // announcement modal's "playing a Knight" line.
    // ALL units a player is fielding this tournament — usually one, but a player
    // who entered AND is the defending champion appears on two teams; the new team
    // is listed first, the Champion slot last. Drives the "X is in!" announcement.
    findUnits(name) {
      const lc = (name || '').toLowerCase();
      const gd = { charStats: this.charStats, itemschanged: this.itemschanged, notAbles: this.notAbles };
      const out = [];
      for (const color in this.items) for (const u of (this.items[color]?.Units || [])) if ((u.Name || '').toLowerCase() === lc) out.push({ unit: u, teamLabel: this.teamByName[color]?.label || '', grade: unitGrade(u, rosterStats, gd) });
      out.sort((a, b) => (a.teamLabel === 'Champion' ? 1 : 0) - (b.teamLabel === 'Champion' ? 1 : 0));
      return out;
    },

    // User dismissed the announcement modal — record this tournament as
    // seen so it doesn't re-fire on refresh.
    dismissAnnouncement() {
      this.showAnnouncement = false;
      this.prefs.seenAnnouncements = markAnnouncementSeen(
        this.prefs.seenAnnouncements,
        this.current_tournament_id,
      );
    },

    // "⚙ Settings" link in the announcement footer — dismiss (marks the
    // tournament seen) and open the gear modal.
    openSettingsFromAnnouncement() {
      this.dismissAnnouncement();
      this.showSettings = true;
    },

    closeWhatsNew() {
      this.showWhatsNew = false;
      markOnboardSeen('intro');
      // New visitors who see the Welcome don't need the spotlight — mark all current updates as seen
      this.markAllUpdatesSeen();
      this.maybeShowAnnouncement();
    },
    startMainTour() {
      this.showWhatsNew = false;
      markOnboardSeen('intro');
      this.markAllUpdatesSeen();
      this.$nextTick(() => { if (this.$refs.tour) this.$refs.tour.begin(); });
    },
    closeSpotlight() {
      this.showSpotlight = false;
      this.markAllUpdatesSeen();
      this.maybeShowAnnouncement();
    },
    markAllUpdatesSeen() {
      const seen = new Set(this.prefs.seenUpdates || []);
      for (const u of SITE_UPDATES) seen.add(u.id);
      this.prefs.seenUpdates = [...seen];
      savePrefs(this.prefs);
    },
    onTourDone() { markOnboardSeen('tour.main'); },

    // Open the fight view comparing two teams (by name), from a bracket cell.
    // a bracket card clicked: show it in the spotlight and scroll up to it
    featureMatch(i) {
      this.spotlightMatch = i;
      const el = document.querySelector('.sp');
      if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    // The bracket's ⚔ (and a ?match= link): that match in the spotlight, its Battle lab open and scrolled to.
    openLab(i) {
      this.spotlightMatch = i;
      this.labRequest += 1;
    },


    // Scroll to a team's section in the team section. $nextTick gives the DOM a
    // beat to un-hide any teams before we measure.
    goto(name) {
      this.$nextTick(() => {
        const el = document.querySelector('.team.' + name);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },

    // Announcement-modal player-name click → find which team the player is on
    // and scroll to it. Dismisses the modal first so it doesn't cover the
    // scroll target.
    teamBrightFor(label) { return teamBright(label); },


    gotoPlayer(playerName) {
      const lower = (playerName || '').toLowerCase();
      const team = this.teams.find(t =>
        (t.roster || []).some(u => (u.Name || '').toLowerCase() === lower)
      );
      this.dismissAnnouncement();
      if (team) {
        for (const t of this.teams) t.hidden = false;
        this.goto(team.name);
      }
    },

  },
};
</script>

<style scoped>
.loading-screen {
  max-width: 500px;
  margin: 80px auto 0;
  padding: 24px 28px 28px;
  background: rgba(12, 54, 80, 0.55);
  border: 2px solid #fff;
  color: #fff;
  /* Body text in the system sans — BossBattle stays only on the heading. */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  text-align: left;
}
.loading-screen h1 {
  font-family: "BossBattle";
  color: #fff;
  -webkit-text-stroke: .1px #000;
  font-size: 30px;
  text-align: center;
  margin: 0 0 6px;
}
.loading-screen h2 {
  font-family: inherit;
  color: #fff;
  font-size: 16px;
  font-weight: normal;
  text-align: center;
  margin: 0 0 20px;
  letter-spacing: 0.05em;
}
.source-list {
  list-style: none;
  padding: 0;
  margin: 0 0 22px;
}
.source-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 5px 0;
  font-family: inherit;
  font-size: 14px;
}
.source-row .dot {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  background: #555;
  border: 1px solid #fff;
}
.source-row .label { flex: 1; }
.source-row .status {
  font-size: 12px;
  opacity: 0.9;
}
/* Hard on/off blink — no smooth easing, like a CRT cursor */
.source-row.loading .dot {
  animation: lux-blink 0.9s steps(2, end) infinite;
}
.source-row.loaded .dot {
  background: #00ff00;
}
.source-row.failed .dot {
  background: #ff0000;
}
.source-row.failed .status {
  color: #ff5555;
}
@keyframes lux-blink {
  0%, 50%  { background: #555; }
  51%, 100% { background: #ccc; }
}
.reload-btn {
  display: block;
  margin: 18px auto 0;
  padding: 5px 16px;
  background: #fff;
  color: #2c3e50;
  border: 1px solid #2c3e50;
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
}
.reload-btn:hover {
  background: #1C6EA4;
  color: #fff;
}

.entrant-link {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
.entrant-link:hover { filter: brightness(1.25); }

/* Skill-drop chip under the Champion match card, in the bracket's champion column (BracketTree's champ-below slot).
   Rarity colors mirror the FFTBG "User Skills" sheet: green = uncommon, blue = rare, neutral = common. */
.skill-drop-chip {
  align-self: center;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 7px 12px;
  width: max-content;
  max-width: 200px;
  border: 1px solid var(--brass-edge);
  border-top: 3px solid #8b93a3;
  border-radius: 4px;
  background: linear-gradient(180deg, var(--panel-top), var(--panel-bottom));
  color: var(--ink);
  text-align: center;
  line-height: 1.2;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45);
}
.skill-drop-chip .sd-label { font-family: var(--cond); font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: 1.6px; color: var(--brass); }
.skill-drop-chip .sd-name { font-family: var(--cond); font-weight: 700; font-size: 18px; color: var(--ink-hi); }
.skill-drop-chip .sd-rarity { font-size: 11px; text-transform: capitalize; opacity: 0.85; }
.skill-drop-chip .sd-own { font-size: 11px; font-weight: bold; margin-top: 3px; padding: 1px 7px; border-radius: 4px; }
.skill-drop-chip .sd-own.owned { color: #8fe0a0; background: rgba(46, 125, 50, 0.22); }
.skill-drop-chip .sd-own.missing { color: var(--brass-hi); background: rgba(217, 180, 94, 0.16); }
.skill-drop-chip .sd-last { font-size: 10px; opacity: 0.65; margin-top: 2px; }
.skill-drop-chip .sd-last.rare { opacity: 1; font-weight: bold; color: var(--brass-hi); }

/* The Clockwork Coliseum banner (ColiseumPromo.vue, which styles itself) between the spotlight and the bracket. */
.cc-promo { margin: 14px 0 6px; }

/* Floating "my skill modifiers" button — follows the screen on the bracket page. */
.my-mods-fab {
  position: fixed;
  bottom: 18px;
  right: 18px;
  z-index: 200;
  padding: 9px 16px;
  background: linear-gradient(180deg, var(--panel-top), var(--navy2));
  color: var(--brass-hi);
  border: 1px solid var(--brass);
  border-radius: 4px;
  font-family: var(--cond);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.55);
}
.my-mods-fab:hover { background: linear-gradient(180deg, #262e44, var(--navy)); }
@media (max-width: 600px) {
  .my-mods-fab { bottom: 12px; right: 12px; padding: 8px 13px; font-size: 13px; }
}
.skill-drop-chip.rarity-uncommon { border-top-color: #6aa84f; }
.skill-drop-chip.rarity-rare     { border-top-color: #4f8fe0; }
.skill-drop-chip.rarity-rare .sd-rarity { color: #9cc4ff; }
.skill-drop-chip.rarity-uncommon .sd-rarity { color: #a6dd8c; }


</style>

<style>
/* Homepage app-controls — live in the shared nav bar's right-side actions slot */
.snav-act { position: relative; display: inline-flex; align-items: center; }
.snav-act .act-tip {
  display: none; position: absolute; top: calc(100% + 8px); right: 0;
  width: max-content; max-width: 240px;
  background: var(--navy); color: var(--ink); border: 1px solid var(--brass-edge); border-radius: 4px;
  padding: 8px 10px; font-size: 12px; line-height: 1.4; text-align: left; white-space: normal;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5); z-index: 402; pointer-events: none;
}
.snav-act:hover .act-tip, .snav-act:focus-within .act-tip { display: block; }
.settings-gear, .help-button, .generator-button, .profile-button {
  width: 34px; height: 32px; box-sizing: border-box; border-radius: 3px;
  background: rgba(217, 180, 94, 0.06); color: var(--brass-hi); border: 1px solid var(--brass-edge);
  line-height: 1; cursor: pointer; font-family: inherit; padding: 0;
}
.settings-gear { font-size: 21px; }
.help-button { font-size: 21px; }
.generator-button { font-size: 19px; display: inline-flex; align-items: center; justify-content: center; text-decoration: none; }
.profile-button { font-size: 19px; display: inline-flex; align-items: center; justify-content: center; text-decoration: none; }
.login-button {
  height: 32px; box-sizing: border-box; padding: 0 12px; cursor: pointer; border: 0; white-space: nowrap;
  font-family: var(--cond); font-weight: 700; font-size: 14px; text-transform: uppercase; letter-spacing: 1.2px;
  color: #1a1408; background: linear-gradient(#e8cf8c, #9c7a3a);
  clip-path: polygon(7px 0, 100% 0, calc(100% - 7px) 100%, 0 100%);
}
.login-button:hover { filter: brightness(1.12); }
.settings-gear:hover, .help-button:hover, .generator-button:hover, .profile-button:hover { background: rgba(217, 180, 94, 0.18); }
.prev-tourney-button {
  width: 34px;
  height: 32px;
  box-sizing: border-box;
  border-radius: 3px;
  border: 1px solid var(--brass-edge);
  background: rgba(217, 180, 94, 0.06);
  color: var(--brass-hi);
  font-size: 17px;
  text-decoration: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
}
.prev-tourney-button:hover { background: rgba(217, 180, 94, 0.18); }
.archive-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  justify-content: center;
  margin: 0 auto 12px;
  padding: 7px 12px;
  max-width: 560px;
  background: linear-gradient(180deg, var(--panel-top), var(--panel-bottom));
  border: 1px solid var(--brass-edge);
  border-radius: 4px;
  color: var(--ink);
  font-size: 13px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45);
}
.archive-label { font-family: var(--cond); font-weight: 700; font-size: 14px; letter-spacing: 1.2px; text-transform: uppercase; color: var(--brass); }
.archive-btn {
  display: inline-block;
  background: transparent;
  color: var(--brass);
  border: 1px solid var(--brass-edge);
  border-radius: 3px;
  padding: 2px 10px;
  font-family: var(--cond);
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
}
.archive-btn:hover { background: rgba(217, 180, 94, 0.12); }
.archive-live { border-color: #5fb86a; color: #8fe0a0; }
.archive-live:hover { background: rgba(95, 184, 106, 0.16); }

/* Goal tracker widget */
.goal-widget { max-width: 620px; margin: 0 auto 12px; padding: 8px 14px; background: linear-gradient(180deg, var(--panel-top), var(--panel-bottom)); border: 1px solid var(--brass-edge); border-radius: 4px; color: var(--ink); text-align: left; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45); }
.goal-dismiss { background: none; border: none; color: var(--mute); font-size: 13px; cursor: pointer; opacity: 0.4; padding: 0 4px 0 0; line-height: 1; }
.goal-dismiss:hover { opacity: 1; color: #fff; }
.goal-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px; }
.goal-label { font-family: var(--cond); font-size: 16px; font-weight: 700; letter-spacing: 0.6px; color: var(--brass-hi); }
.goal-status { font-size: 12px; opacity: 0.7; font-variant-numeric: tabular-nums; }
.goal-profile { font-size: 12px; color: var(--brass); text-decoration: none; margin-left: auto; }
.goal-profile:hover { text-decoration: underline; }
.goal-rolling { font-size: 12px; margin-top: 5px; color: #6ee7a0; }
.goal-remaining { display: flex; flex-wrap: wrap; gap: 3px; margin-top: 6px; align-items: center; }
.goal-remain-label { font-size: 10px; opacity: 0.5; margin-right: 2px; }
.goal-remain-item { font-size: 11px; padding: 1px 7px; border-radius: 3px; background: rgba(255, 255, 255, 0.06); border: 1px solid var(--line); white-space: nowrap; }
.goal-remain-item.hit { background: rgba(110, 231, 160, 0.2); color: #6ee7a0; border: 1px solid rgba(110, 231, 160, 0.4); }
.goal-remain-more { font-size: 10px; opacity: 0.5; }
.goal-hint { font-size: 11px; opacity: 0.6; margin-top: 4px; font-style: italic; }

.headsup-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1100;
  background: linear-gradient(#e8cf8c, #9c7a3a);
  color: #1a1408;
  text-align: center;
  padding: 9px 16px;
  font-family: var(--cond);
  font-weight: 600;
  font-size: 18px;
  letter-spacing: 0.4px;
  cursor: pointer;
  border-bottom: 1px solid var(--brass-edge);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}
.headsup-banner .hu-dismiss { opacity: 0.75; font-size: 12px; }

/* Unscoped so it applies regardless of where the banner lands in the layout. */
.test-mode-banner {
  display: inline-block;
  background: #8e2b22;
  color: #fff;
  padding: 3px 12px;
  margin: 0 0 8px;
  font-family: var(--cond);
  font-weight: 700;
  letter-spacing: 1.2px;
  font-size: 15px;
  border: 1px solid #e07a6e;
  border-radius: 3px;
}

/* The meme-tournament strip above the spotlight (loadTournamentDump). */
.lx-meme {
  display: flex; flex-wrap: wrap; align-items: baseline; justify-content: center; gap: 4px 10px; margin: 0 auto 12px; padding: 6px 14px;
  max-width: 720px; background: linear-gradient(180deg, var(--panel-top), var(--panel-bottom)); border: 1px solid var(--brass-edge);
  border-left: 3px solid var(--brass); border-radius: 4px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45); color: var(--ink); cursor: help;
}
.lx-meme-icon { font-size: 18px; align-self: center; }
.lx-meme-name { font-family: var(--display); font-weight: 700; font-size: 17px; letter-spacing: 0.6px; color: var(--brass-hi); }
.lx-meme-note { font-family: var(--cond); font-weight: 600; font-size: 13px; letter-spacing: 1.2px; text-transform: uppercase; color: var(--mute); }
.lx-meme-more { font-family: var(--cond); font-size: 14px; color: var(--brass); text-decoration: none; cursor: pointer; }
.lx-meme-more:hover { color: var(--brass-hi); text-decoration: underline; }

/* The team section's controls bar (show/hide chips + your highlighted entrants/snubs). */
.lx-teambar {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px 14px; margin: 18px 0 4px; padding: 7px 12px;
  background: linear-gradient(180deg, var(--panel-top), var(--panel-bottom)); border: 1px solid var(--brass-edge); border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45); text-align: left;
}
.lx-teambar .tb-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.lx-teambar .tb-chip {
  padding: 2px 10px; border-radius: 3px; cursor: pointer; font-family: var(--cond); font-weight: 700; font-size: 15px;
  letter-spacing: 0.6px; text-transform: uppercase; color: var(--ink-hi); background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--line); border-left: 4px solid var(--tc);
}
.lx-teambar .tb-chip:hover { background: rgba(217, 180, 94, 0.12); }
.lx-teambar .tb-chip.hiding { opacity: 0.4; text-decoration: line-through; border-left-color: #444; }
.lx-teambar .tb-wr { display: inline-flex; align-items: center; gap: 6px; padding: 2px 10px; border-radius: 3px; cursor: pointer;
  font-family: var(--cond); font-weight: 600; font-size: 14px; color: var(--mute); background: rgba(255, 255, 255, 0.04); border: 1px solid var(--line); }
.lx-teambar .tb-wr:hover { background: rgba(217, 180, 94, 0.12); color: var(--ink-hi); }
.lx-teambar .tb-wr.on { color: var(--ink-hi); border-color: var(--brass-edge); }
.tb-wr-dot { width: 18px; height: 8px; border-radius: 4px; background: #555; }
.tb-wr.on .tb-wr-dot { background: linear-gradient(90deg, hsl(0, 70%, 58%), #888 50%, hsl(130, 70%, 58%)); }
.lx-teambar .tb-mode { display: inline-flex; border: 1px solid var(--brass-edge); border-radius: 3px; overflow: hidden; }
.lx-teambar .tb-mode button { padding: 2px 10px; border: 0; border-left: 1px solid var(--brass-edge); background: none; cursor: pointer; font-family: var(--cond); font-weight: 600; font-size: 13px; letter-spacing: .5px; text-transform: uppercase; color: var(--mute); }
.lx-teambar .tb-mode button:first-child { border-left: 0; }
.lx-teambar .tb-mode button:hover { color: var(--ink-hi); background: rgba(217, 180, 94, 0.08); }
.lx-teambar .tb-mode button.on { color: var(--brass-hi); background: rgba(217, 180, 94, 0.18); }
.lx-teambar .tb-entries { display: flex; flex-wrap: wrap; gap: 4px 14px; margin-left: auto; font-size: 14px; }
.lx-teambar .tb-in { color: #8fe0a0; }
.lx-teambar .tb-snub { color: var(--mute); font-style: italic; }

</style>



